import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { ChevronDown, Pause, Play } from "lucide-react";
import { api, mediaUrl } from "@/lib/api";
import { usePlayer } from "@/context/PlayerContext";
import { CountdownBanner } from "@/components/CountdownBanner";
import { Reveal } from "@/components/Reveal";
import { ShareTrackButton } from "@/components/ShareTrackButton";
import { SocialIcon } from "@/components/SocialIcons";

const fmtDate = (d) => {
  if (!d) return "";
  if (/^\d{4}$/.test(d)) return d;
  try {
    return new Date(d).toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" });
  } catch {
    return d;
  }
};

const PlayBtn = ({ t, size = 16, testid }) => {
  const { playTrack, track, playing } = usePlayer();
  const active = track?.id === t.id;
  return (
    <button
      onClick={(e) => {
        e.stopPropagation();
        playTrack(t);
      }}
      data-testid={testid || `track-play-button-${t.id}`}
      aria-label={`Play ${t.title} preview`}
      className="w-11 h-11 shrink-0 rounded-full flex items-center justify-center text-navy-950 transition-transform hover:scale-110"
      style={{ background: "linear-gradient(135deg,#F59E0B,#D4AF37 50%,#B38728)" }}
    >
      {active && playing ? <Pause size={size} fill="currentColor" /> : <Play size={size} fill="currentColor" className="ml-0.5" />}
    </button>
  );
};

const TrackRow = ({ t, index }) => {
  const { openDetails } = usePlayer();
  return (
    <div
      className="flex items-center gap-4 px-4 sm:px-6 py-4 border-b border-white/5 hover:bg-white/[0.03] transition-colors group"
      data-testid={`track-row-${t.id}`}
    >
      <PlayBtn t={t} size={14} />
      <button onClick={() => openDetails(t)} className="flex-1 text-left min-w-0" data-testid={`track-open-${t.id}`}>
        <p className="font-serif text-slate-100 truncate group-hover:text-gold-200 transition-colors">{t.title}</p>
        <p className="text-[11px] text-slate-500">{fmtDate(t.release_date)}</p>
      </button>
      <span className="text-[11px] text-gold-400/70 tracking-widest uppercase hidden sm:block">1:00 preview</span>
      <ShareTrackButton track={t} compact testid={`track-share-${t.id}`} />
    </div>
  );
};

const AlbumCard = ({ album, open, onToggle }) => {
  return (
    <div className="rounded-2xl overflow-hidden border border-white/5 bg-navy-900/60" data-testid={`album-card-${album.id}`}>
      <button
        onClick={onToggle}
        className="w-full flex items-center gap-5 sm:gap-8 p-5 sm:p-7 text-left"
        data-testid={`album-expand-${album.id}`}
        aria-expanded={open}
      >
        <img
          src={mediaUrl(album.cover_url)}
          alt={`${album.title} cover`}
          className="w-20 h-20 sm:w-28 sm:h-28 rounded-xl object-cover border border-gold-400/25"
        />
        <div className="flex-1 min-w-0">
          <p className="eyebrow !text-[10px] mb-1">Album</p>
          <h3 className="font-serif text-2xl sm:text-3xl text-slate-50">{album.title}</h3>
          <p className="text-sm text-slate-400 mt-1 font-light line-clamp-2">{album.blurb}</p>
          <p className="text-xs text-slate-500 mt-2">
            {album.tracks?.length || 0} tracks{album.release_date ? ` · ${fmtDate(album.release_date)}` : ""}
          </p>
        </div>
        <ChevronDown
          size={22}
          className={`text-gold-400 transition-transform duration-500 shrink-0 ${open ? "rotate-180" : ""}`}
        />
      </button>
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
            className="overflow-hidden border-t border-gold-400/10"
          >
            {(album.spotify_url || album.apple_url) && (
              <div className="flex flex-wrap gap-3 px-4 sm:px-5 pt-4 pb-1">
                {album.spotify_url && (
                  <a href={album.spotify_url} target="_blank" rel="noopener noreferrer" data-testid={`album-spotify-${album.id}`}
                    className="btn-ghost-gold !px-5 !py-2.5 text-xs inline-flex items-center gap-2">
                    <SocialIcon name="spotify" size={15} /> Listen on Spotify
                  </a>
                )}
                {album.apple_url && (
                  <a href={album.apple_url} target="_blank" rel="noopener noreferrer" data-testid={`album-apple-${album.id}`}
                    className="btn-ghost-gold !px-5 !py-2.5 text-xs inline-flex items-center gap-2">
                    <SocialIcon name="apple" size={15} /> Listen on Apple Music
                  </a>
                )}
              </div>
            )}
            {(album.tracks || []).map((t, i) => (
              <TrackRow key={t.id} t={t} index={i} />
            ))}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default function Music() {
  const [albums, setAlbums] = useState([]);
  const [releases, setReleases] = useState([]);
  const [openAlbum, setOpenAlbum] = useState(null);
  const [searchParams] = useSearchParams();

  useEffect(() => {
    const al = searchParams.get("album");
    if (al && albums.some((a) => a.id === al)) {
      setOpenAlbum(al);
      setTimeout(() => {
        document.querySelector(`[data-testid="album-card-${al}"]`)?.scrollIntoView({ behavior: "smooth", block: "center" });
      }, 300);
    }
  }, [searchParams, albums]);

  useEffect(() => {
    api.get("/albums").then((r) => setAlbums(r.data.filter((a) => a.tracks?.length))).catch(() => {});
    api.get("/releases").then((r) => setReleases(r.data)).catch(() => {});
  }, []);

  // General list stacks newest-first (a release stack); the catalogue position number stays a permanent identity.
  const singles = [...releases].sort((a, b) =>
    String(b.created_at || b.release_date || "").localeCompare(String(a.created_at || a.release_date || "")) || a.order - b.order
  );

  return (
    <div className="pt-28 pb-24" data-testid="music-page">
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <Reveal>
          <p className="eyebrow mb-3">The Catalogue</p>
          <h1 className="font-serif text-4xl sm:text-6xl text-slate-50">
            Music Beyond <span className="text-gold-gradient">The Horizon</span>
          </h1>
          <p className="mt-5 max-w-2xl text-slate-300/85 font-light leading-relaxed">
            Every track opens with a 60-second preview. Tap any title for lyrics, the story
            behind the song, and links to the full release on Spotify and Apple Music.
          </p>
        </Reveal>

        <CountdownBanner />

        {albums.length > 0 && (
          <div className="mt-16 space-y-8" data-testid="albums-section">
            {albums.map((a) => (
              <Reveal key={a.id}>
                <AlbumCard album={a} open={openAlbum === a.id} onToggle={() => setOpenAlbum(openAlbum === a.id ? null : a.id)} />
              </Reveal>
            ))}
          </div>
        )}

        <div className="mt-16" data-testid="singles-section">
          <Reveal>
            <h2 className="font-serif text-2xl sm:text-4xl text-slate-50 mb-8">Singles & Releases</h2>
          </Reveal>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
            {singles.map((t, i) => (
              <SingleCard key={t.id} t={t} i={i} />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

const SingleCard = ({ t, i }) => {
  const { openDetails, track } = usePlayer();
  const active = track?.id === t.id;
  return (
    <Reveal delay={(i % 3) * 0.1}>
      <div
        className={`group relative rounded-2xl overflow-hidden border bg-navy-900/60 transition-colors duration-500 ${
          active ? "border-gold-400/60 shadow-[0_0_30px_rgba(212,175,55,0.2)]" : "border-white/5 hover:border-gold-400/40"
        }`}
        data-testid={`single-card-${t.id}`}
      >
        <div onClick={() => openDetails(t)} role="button" tabIndex={0}
          onKeyDown={(e) => { if (e.key === "Enter") openDetails(t); }}
          className="block w-full text-left cursor-pointer" data-testid={`single-open-${t.id}`}>
          <div className="relative aspect-square overflow-hidden">
            <img
              src={mediaUrl(t.cover_url)}
              alt={`${t.title} cover art`}
              className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-navy-950/90 via-navy-950/10 to-transparent" />
            <div className="absolute top-3 right-3 opacity-100 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity duration-300">
              <ShareTrackButton track={t} compact testid={`card-share-${t.id}`} className="bg-navy-950/70 backdrop-blur" />
            </div>
            <div className="absolute bottom-4 right-4 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
              <PlayBtn t={t} size={16} />
            </div>
          </div>
          <div className="p-5">
            <Link to={`/music/${t.id}`} data-testid={`track-link-${t.id}`}>
              <h3 className="font-serif text-lg text-slate-100 group-hover:text-gold-200 transition-colors hover:underline underline-offset-4">{t.title}</h3>
            </Link>
            <p className="text-xs text-slate-500 mt-1">{fmtDate(t.release_date)}</p>
          </div>
        </div>
      </div>
    </Reveal>
  );
};
